import React, { useCallback, useDeferredValue, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputBase,
  Link,
  Skeleton,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  CheckCircleOutline,
  CloudOffOutlined,
  ErrorOutline,
  HelpOutline,
  PostAddOutlined,
} from '@mui/icons-material';
import { useEditorChrome, useWorkspaceLayout } from '../context/WorkspaceLayoutContext';
import { useParams, useNavigate, useBlocker } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/EnterpriseAuthContext';
import { useUsage } from '../context/UsageContext';
import { hasReachedLimit } from '../services/usageService';
import EnhancedRichTextEditor from './EnhancedRichTextEditor';
import { useActiveEditor } from '../context/ActiveEditorContext';
import { analyzeTemplateQuality } from '../utils/templateQualityScore';
import { parseTemplateFields } from '../utils/templateFields';
import { registerUnsavedWorkFlusher } from '../utils/navigation';
import { formatShortcut } from '../hooks/useWorkspaceShortcuts';
import useAutosave, { hasActiveSession } from './editor/useAutosave';
import TemplateQualityChip from './templates/TemplateQualityChip';
import { EditorJobsButton, EditorNavButton, InkyReopenButton } from './layout/EditorChromeButtons';

/**
 * Starter content for a new template. Field instructions are saved in the brace storage form
 * (`{…}` inside the field element) and no `{{variables}}` are used, so templates created now
 * also generate correctly with the generation pipeline that predates template fields.
 */
const NEW_TEMPLATE_CONTENT = `<h1>Project Requirements Document</h1>
<h2>Executive Summary</h2>
<div data-tpl-field="f_00000001" data-kind="rich" data-required="true" data-source="requirements" data-max-words="250">{Write a 2-3 paragraph summary of the project goals and expected outcomes based on the requirements.}</div>
<h2>Requirements Overview</h2>
<div data-tpl-field="f_00000002" data-kind="list" data-required="true" data-source="requirements">{List the main functional requirements in order of priority.}</div>
<h2>Implementation Timeline</h2>
<div data-tpl-field="f_00000003" data-kind="table_rows" data-required="false" data-source="sessions">{?Create a project timeline with major milestones based on the requirements and meeting decisions.}</div>
<h2>Success Criteria</h2>
<div data-tpl-field="f_00000004" data-kind="list" data-required="true" data-source="requirements">{Define measurable success criteria for the project, tied to the requirements above.}</div>`;

/** Header height on one row (px); the measured height is used when it wraps on narrow screens. */
const HEADER_HEIGHT = 64;
/** Idle time after the last edit before an existing template saves itself. */
export const TEMPLATE_AUTOSAVE_DELAY_MS = 1500;

/** A failed template write, classified for autosave (conflict / permanent / transient). */
export class TemplateSaveError extends Error {
  /**
   * @param {{error?: string, code?: string, status?: number, conflict?: boolean, data?: object}} result - updateTemplate result.
   */
  constructor(result) {
    super(result?.error || 'Failed to save template.');
    this.name = 'TemplateSaveError';
    this.code = result?.code || null;
    this.status = result?.status || null;
    this.conflict = Boolean(result?.conflict);
    this.serverTemplate = result?.conflict ? result.data || null : null;
  }
}

/** Error codes that retrying cannot fix: the row is gone or not ours, or we are signed out. */
const PERMANENT_CODES = new Set(['NOT_FOUND', 'NOT_AUTHENTICATED', 'ACCOUNT_CHANGED', 'PGRST116', 'PGRST301', 'PGRST302', '42501', '42P01']);

/**
 * Whether a template save failure is permanent (autosave stops and waits for the user).
 * @param {unknown} error - Error from the save.
 * @returns {boolean} True for permanent failures.
 */
export function isPermanentTemplateError(error) {
  if (!error) return false;
  if (PERMANENT_CODES.has(error.code)) return true;
  return error.status === 401 || error.status === 403;
}

/**
 * Serialize the saved parts of a template (the autosave baseline compares these strings).
 * @param {{name: string, description: string, content: string}} snapshot - Template parts.
 * @returns {string} Serialized form.
 */
const serializeTemplate = ({ name, description, content }) => JSON.stringify([name.trim(), description || '', content || '']);

/**
 * Validation messages for a template snapshot.
 * @param {{name: string, content: string}} snapshot - Template parts.
 * @returns {{name?: string, content?: string}} Errors (empty when valid).
 */
const validateTemplate = ({ name, content }) => {
  const errors = {};
  if (!name.trim()) errors.name = 'Template name is required';
  const hasText = Boolean(content) && content.replace(/<[^>]+>/g, '').trim() !== '';
  const hasStructure = Boolean(content) && /data-tpl-field|<img|<table|data-mermaid-block/.test(content);
  if (!hasText && !hasStructure) errors.content = 'Template content is required';
  return errors;
};

const sameErrors = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Editor-shaped loading placeholder with the real navigation (menu and back), since the shell's
 * top bar and sidebar are already hidden for this page.
 * @param {{onBack: () => void}} props - Props.
 * @returns {JSX.Element}
 */
const TemplateEditorSkeleton = ({ onBack }) => (
  <Box>
    <Box
      component="header"
      sx={{
        minHeight: HEADER_HEIGHT,
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 0.5, sm: 1 },
        px: { xs: 0.5, sm: 1.5 },
        bgcolor: 'var(--q-surface, #FFFFFF)',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <EditorNavButton />
      <Tooltip title="Back to templates">
        <IconButton aria-label="Back to templates" onClick={onBack}>
          <ArrowBack />
        </IconButton>
      </Tooltip>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Skeleton variant="text" width={120} height={16} />
        <Skeleton variant="text" width={240} height={26} />
      </Box>
    </Box>
    <Box role="progressbar" aria-label="Loading template" aria-busy="true" sx={{ px: { xs: 1, sm: 2 }, py: 1.5 }}>
      <Skeleton variant="rounded" height={40} sx={{ mb: 2, borderRadius: 999 }} />
      <Skeleton variant="rounded" height={560} sx={{ maxWidth: 794, mx: 'auto' }} />
    </Box>
  </Box>
);

/**
 * The single save status, shown next to the template name. Its tooltip (also on keyboard
 * focus) gives the last saved time or the exact reason a save failed.
 * @param {{isEditing: boolean, status: string, newDirty: boolean, creating: boolean, error: string|null, lastSavedAt: number|null, halted: boolean}} props - Save state.
 * @returns {JSX.Element} Label.
 */
const SaveStatusLabel = ({ isEditing, status, newDirty, creating, error, lastSavedAt, halted }) => {
  let icon = null;
  let label;
  let detail = null;
  let color = 'text.secondary';
  if (!isEditing) {
    if (creating) {
      icon = <CircularProgress size={11} thickness={6} color="inherit" />;
      label = 'Creating…';
    } else {
      label = 'Not saved yet';
      detail = newDirty ? 'Create the template to save it' : null;
    }
  } else if (status === 'saving') {
    icon = <CircularProgress size={11} thickness={6} color="inherit" />;
    label = 'Saving…';
  } else if (status === 'error') {
    icon = <ErrorOutline sx={{ fontSize: 14 }} />;
    label = halted ? 'Not saved' : 'Save failed, retrying';
    color = 'var(--q-danger-text)';
    detail = `${error || 'The last save failed.'} ${halted ? `Autosave is paused: press ${formatShortcut('Mod+S')} to try again.` : 'Retrying automatically.'}`;
  } else if (status === 'offline') {
    icon = <CloudOffOutlined sx={{ fontSize: 14 }} />;
    label = 'Offline, will retry';
    color = 'var(--q-warning-text)';
    detail = 'You are offline. Changes save when you reconnect.';
  } else if (status === 'conflict') {
    icon = <ErrorOutline sx={{ fontSize: 14 }} />;
    label = 'Changed elsewhere';
    color = 'var(--q-danger-text)';
    detail = error || 'This template was changed in another tab or by a teammate.';
  } else if (status === 'dirty') {
    label = 'Unsaved changes';
    detail = 'Saving shortly';
  } else {
    icon = <CheckCircleOutline sx={{ fontSize: 14 }} />;
    label = 'Saved';
    color = 'var(--q-success-text)';
    detail = lastSavedAt
      ? `Last saved at ${new Date(lastSavedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
      : 'All changes saved';
  }
  const element = (
    <Box
      component="span"
      role="status"
      aria-live="polite"
      tabIndex={detail ? 0 : undefined}
      data-testid="template-save-status"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flex: 'none', fontSize: 12.5, whiteSpace: 'nowrap', color, borderRadius: '4px' }}
    >
      {icon}
      {label}
    </Box>
  );
  return detail ? <Tooltip title={detail} describeChild>{element}</Tooltip> : element;
};

/**
 * Template authoring page: AI fields (data-tpl-field), variables, locked blocks, a live
 * (deferred) quality score in the header, autosave for saved templates (useAutosave: backoff,
 * permanent-error stop, conflict check against `updated_at`, no writes after unmount unless the
 * session and workspace are unchanged), a "Create template" action for new ones and an
 * unsaved-changes guard.
 *
 * The page uses the editor chrome: the shell's top bar and sidebar step aside (the header has
 * back navigation, a menu button and the AI jobs control), Inky's panel is the only Inky
 * surface, and help lives in the toolbar's More menu.
 * @returns {JSX.Element}
 */
function TemplateEditor() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { state, actions } = useApp();
  const { state: authState } = useAuth();
  const { refreshUsage } = useUsage();
  const { openInky } = useWorkspaceLayout();
  useEditorChrome();
  const isEditing = Boolean(templateId && templateId !== 'new');
  const accountId = authState?.activeAccount?.id || null;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [initialContent, setInitialContent] = useState('');
  const [liveContent, setLiveContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadKey, setLoadKey] = useState(null);
  const [legacyConverted, setLegacyConverted] = useState(0);
  const [creating, setCreating] = useState(false);
  const [newDirty, setNewDirty] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [validation, setValidation] = useState({});
  const [createError, setCreateError] = useState(null);
  const [limitReachedOpen, setLimitReachedOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [editorReady, setEditorReady] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(HEADER_HEIGHT);
  const editorRef = useRef(null);
  const headerRef = useRef(null);
  const nameRef = useRef('');
  const descriptionRef = useRef('');
  const liveContentRef = useRef('');
  nameRef.current = name;
  descriptionRef.current = description;
  liveContentRef.current = liveContent;
  const actionsRef = useRef(actions);
  actionsRef.current = actions;
  const templateIdRef = useRef(templateId);
  templateIdRef.current = templateId;
  const accountIdRef = useRef(accountId);
  accountIdRef.current = accountId;
  /** Workspace the template was opened in; saves never cross into another one. */
  const loadAccountIdRef = useRef(null);
  /** `updated_at` of the version this page last loaded or saved (optimistic concurrency). */
  const updatedAtRef = useRef(null);
  /** The next save overwrites a newer server version ("Keep mine"). */
  const forceNextSaveRef = useRef(false);
  /** Set when a forced ("Keep mine") write actually reached the server. */
  const forcedWriteDoneRef = useRef(false);
  /** Server version reported by the last conflict (for "Reload theirs"). */
  const conflictTemplateRef = useRef(null);
  /** New template only: unsaved edits since the page opened. */
  const newDirtyRef = useRef(false);
  /** Incremented on every edit (detects edits made while a template is being created). */
  const editGenerationRef = useRef(0);
  /** Set while switching /templates/new to the saved template's URL (same page, no prompt). */
  const bypassBlockerRef = useRef(false);
  const loadedIdRef = useRef(null);
  /** After "Create template": the created snapshot, and whether edits arrived during the create. */
  const pendingCreatedRef = useRef(null);
  const baselineKeyRef = useRef(null);
  const createPromiseRef = useRef(null);
  const { registerEditor, unregisterEditor } = useActiveEditor();

  const deferredContent = useDeferredValue(liveContent);
  const templateQuality = useMemo(() => analyzeTemplateQuality(deferredContent), [deferredContent]);

  /**
   * Current template parts, read from the editor at call time.
   * @returns {{name: string, description: string, content: string}} Snapshot.
   */
  const readSnapshot = useCallback(() => {
    editorRef.current?.flush?.();
    const content = editorRef.current?.getHTML?.() ?? liveContentRef.current;
    return { name: nameRef.current, description: descriptionRef.current, content };
  }, []);

  /**
   * Validate and update the inline validation messages.
   * @param {{name: string, content: string}} snapshot - Template parts.
   * @returns {boolean} True when valid.
   */
  const applyValidation = useCallback((snapshot) => {
    const next = validateTemplate(snapshot);
    setValidation((prev) => (sameErrors(prev, next) ? prev : next));
    return Object.keys(next).length === 0;
  }, []);

  // ---------- autosave (saved templates) ----------
  const getContent = useCallback(() => {
    const snapshot = readSnapshot();
    if (!applyValidation(snapshot)) return null;
    return serializeTemplate(snapshot);
  }, [readSnapshot, applyValidation]);

  const save = useCallback(async (serialized) => {
    const [savedName, savedDescription, content] = JSON.parse(serialized);
    if (loadAccountIdRef.current && accountIdRef.current !== loadAccountIdRef.current) {
      throw new TemplateSaveError({ error: 'You switched workspace, so this template was not saved here.', code: 'ACCOUNT_CHANGED' });
    }
    const force = forceNextSaveRef.current;
    forceNextSaveRef.current = false;
    const result = await actionsRef.current.updateTemplate(
      templateIdRef.current,
      { name: savedName, description: savedDescription, content },
      { expectedUpdatedAt: updatedAtRef.current, force, silent: true }
    );
    if (!result || result.success === false) {
      const error = new TemplateSaveError(result || { error: 'Update failed' });
      if (error.conflict) conflictTemplateRef.current = error.serverTemplate;
      throw error;
    }
    if (result.data?.updatedAt) updatedAtRef.current = result.data.updatedAt;
    if (force) forcedWriteDoneRef.current = true;
    setLegacyConverted(0);
  }, []);

  /** Final save on unmount only with a live session in the workspace the template belongs to. */
  const canSaveOnUnmount = useCallback(async () => {
    if (loadAccountIdRef.current && accountIdRef.current !== loadAccountIdRef.current) return false;
    return hasActiveSession();
  }, []);

  const autosave = useAutosave({
    getContent,
    save,
    delay: TEMPLATE_AUTOSAVE_DELAY_MS,
    enabled: isEditing && !loading && editorReady,
    isConflictError: (error) => Boolean(error && error.conflict),
    isPermanentError: isPermanentTemplateError,
    canSaveOnUnmount,
  });
  const autosaveRef = useRef(autosave);
  autosaveRef.current = autosave;
  const isEditingRef = useRef(isEditing);
  isEditingRef.current = isEditing;

  /**
   * Whether anything typed so far is not saved yet.
   * @returns {boolean} True with unsaved edits.
   */
  const hasUnsavedWork = useCallback(
    () => (isEditingRef.current ? autosaveRef.current.isDirty() : newDirtyRef.current),
    []
  );

  /**
   * Record an edit: saved templates schedule an autosave; new ones only track that they changed.
   * @returns {void}
   */
  const markDirty = useCallback(() => {
    editGenerationRef.current += 1;
    if (isEditingRef.current) {
      autosaveRef.current.markDirty();
      return;
    }
    if (!newDirtyRef.current) {
      newDirtyRef.current = true;
      setNewDirty(true);
    }
  }, []);

  // Expose the template body to Inky while the editor exists.
  useEffect(() => {
    if (!editorReady) return undefined;
    registerEditor(editorRef);
    return () => unregisterEditor(editorRef);
  }, [editorReady, registerEditor, unregisterEditor]);

  // Load once per template id (later template list updates, e.g. after saving, are ignored).
  useEffect(() => {
    const key = isEditing ? templateId : 'new';
    if (loadedIdRef.current === key) return;
    if (isEditing && !state.initialLoadComplete) {
      setLoading(true);
      return;
    }
    let content = NEW_TEMPLATE_CONTENT;
    if (isEditing) {
      const template = state.templates.find((t) => t.id === templateId);
      if (!template) {
        navigate('/templates');
        return;
      }
      setName(template.name || '');
      setDescription(template.description || '');
      content = template.content || '';
      updatedAtRef.current = template.updatedAt || null;
      setLegacyConverted(parseTemplateFields(content).legacyCount);
    } else {
      setName('');
      setDescription('');
      updatedAtRef.current = null;
      setLegacyConverted(0);
    }
    loadedIdRef.current = key;
    loadAccountIdRef.current = accountIdRef.current;
    baselineKeyRef.current = null;
    newDirtyRef.current = false;
    setNewDirty(false);
    setInitialContent(content);
    setLiveContent(content);
    setLoadKey(key);
    setLoading(false);
  }, [templateId, isEditing, state.templates, state.initialLoadComplete, navigate]);

  // Just created: the created version is the saved baseline; edits typed during the create are
  // saved by autosave right away. (Runs before the load baseline effect below.)
  const { setBaseline, markDirty: autosaveMarkDirty } = autosave;
  useEffect(() => {
    if (!isEditing || !editorReady || !pendingCreatedRef.current) return;
    const { serialized, editedDuringCreate } = pendingCreatedRef.current;
    pendingCreatedRef.current = null;
    baselineKeyRef.current = loadKey;
    setBaseline(serialized);
    if (editedDuringCreate) autosaveMarkDirty();
  }, [isEditing, editorReady, loadKey, setBaseline, autosaveMarkDirty]);

  // Loaded: what the editor shows now (including migrated legacy placeholders) is the baseline.
  useEffect(() => {
    if (!isEditing || !editorReady || loading || baselineKeyRef.current === loadKey) return;
    baselineKeyRef.current = loadKey;
    setBaseline(serializeTemplate(readSnapshot()));
  }, [isEditing, editorReady, loading, loadKey, setBaseline, readSnapshot]);

  /** Latest createTemplate (declared below), so saveNow can create a new template. */
  const createTemplateRef = useRef(null);
  /**
   * Save now (Mod-S, "Save now", the logout flush). New templates are created instead.
   * @param {{toastOnSuccess?: boolean}} [opts] - Show "Template saved".
   * @returns {Promise<boolean>} True when everything typed so far is saved.
   */
  const saveNow = useCallback(async ({ toastOnSuccess = true } = {}) => {
    if (!isEditingRef.current) return createTemplateRef.current ? createTemplateRef.current() : false;
    const ok = await autosaveRef.current.saveNow();
    if (ok && toastOnSuccess) setToast('Template saved');
    return ok;
  }, []);

  /**
   * Create a new template (one at a time). The page then moves to the new template's URL and
   * keeps editing there with autosave.
   * @returns {Promise<boolean>} True when created with everything typed so far.
   */
  const createTemplate = useCallback(async () => {
    if (createPromiseRef.current) return createPromiseRef.current;
    const run = (async () => {
      const snapshot = readSnapshot();
      if (!applyValidation(snapshot)) return false;
      setCreateError(null);
      setCreating(true);
      const generation = editGenerationRef.current;
      try {
        const usageData = await refreshUsage();
        if (usageData && hasReachedLimit(usageData, 'templates')) {
          setLimitReachedOpen(true);
          return false;
        }
        const result = await actionsRef.current.createTemplate({
          name: snapshot.name.trim(),
          description: snapshot.description,
          content: snapshot.content,
        });
        if (!result || result.success === false) throw new Error(result?.error || 'Create failed');
        refreshUsage();
        const newId = result.data?.id;
        if (!newId) {
          newDirtyRef.current = false;
          navigate('/templates');
          return true;
        }
        const editedDuringCreate = editGenerationRef.current !== generation;
        updatedAtRef.current = result.data?.updatedAt || null;
        loadAccountIdRef.current = accountIdRef.current;
        pendingCreatedRef.current = { serialized: serializeTemplate(snapshot), editedDuringCreate };
        newDirtyRef.current = false;
        setNewDirty(false);
        loadedIdRef.current = newId;
        bypassBlockerRef.current = true;
        navigate(`/templates/${newId}/edit`, { replace: true });
        setToast(editedDuringCreate ? 'Template created. Saving your newer edits…' : 'Template created');
        return !editedDuringCreate;
      } catch (error) {
        console.error('Error creating template:', error);
        setCreateError('Failed to create template. Please try again.');
        return false;
      } finally {
        setCreating(false);
      }
    })();
    createPromiseRef.current = run;
    try {
      return await run;
    } finally {
      createPromiseRef.current = null;
    }
  }, [applyValidation, navigate, readSnapshot, refreshUsage]);
  createTemplateRef.current = createTemplate;

  // Leaving with unsaved edits: a saved template saves first and then leaves; a new template
  // (or a save that fails) asks before discarding.
  useEffect(() => {
    bypassBlockerRef.current = false;
  }, [templateId]);
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => hasUnsavedWork()
      && !bypassBlockerRef.current
      && currentLocation.pathname !== nextLocation.pathname
  );
  const blockerBusyRef = useRef(false);
  useEffect(() => {
    if (blocker.state !== 'blocked' || blockerBusyRef.current) return;
    blockerBusyRef.current = true;
    const settle = async () => {
      const saved = isEditingRef.current ? await autosaveRef.current.saveNow() : false;
      if (saved || window.confirm('You have unsaved changes. Leave without saving?')) blocker.proceed();
      else blocker.reset();
      blockerBusyRef.current = false;
    };
    settle();
  }, [blocker]);

  // Refresh/tab close: useAutosave guards saved templates; a new template only warns.
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isEditingRef.current || !newDirtyRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Actions that unmount the page without a router navigation (logging out) save pending
  // edits through this first; the route blocker never sees them.
  useEffect(
    () => registerUnsavedWorkFlusher(async () => (hasUnsavedWork() ? saveNow({ toastOnSuccess: false }) : true)),
    [hasUnsavedWork, saveNow]
  );

  // Mod-S anywhere on the page (the editor handles it itself while it has focus).
  useEffect(() => {
    const onKey = (event) => {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 's') {
        event.preventDefault();
        saveNow();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [saveNow]);

  // The header wraps on narrow screens; the toolbar sticks right below its real height.
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const update = () => {
      const next = Math.round(el.getBoundingClientRect().height) || HEADER_HEIGHT;
      setHeaderHeight((prev) => (prev === next ? prev : next));
    };
    update();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [loading]);

  /**
   * Generate a document from this template, saving pending edits first.
   * @returns {Promise<void>}
   */
  const handleGenerate = useCallback(async () => {
    if (autosaveRef.current.isDirty()) {
      const saved = await autosaveRef.current.saveNow();
      if (!saved) {
        setToast('Save the template before generating a document.');
        return;
      }
    }
    navigate(`/generate?templateId=${encodeURIComponent(templateId)}`);
  }, [navigate, templateId]);

  /** Conflict: load the other version (drops this page's unsaved edits). */
  const handleReloadTheirs = useCallback(() => {
    const server = conflictTemplateRef.current || state.templates.find((t) => t.id === templateId);
    if (!server) return;
    conflictTemplateRef.current = null;
    setName(server.name || '');
    setDescription(server.description || '');
    nameRef.current = server.name || '';
    descriptionRef.current = server.description || '';
    editorRef.current?.loadContent?.(server.content || '');
    setLiveContent(server.content || '');
    liveContentRef.current = server.content || '';
    updatedAtRef.current = server.updatedAt || null;
    autosave.clearConflict();
    setBaseline(serializeTemplate(readSnapshot()));
  }, [autosave, readSnapshot, setBaseline, state.templates, templateId]);

  /**
   * Conflict: overwrite the other version with this page's content. Always performs the forced
   * write (even if the content equals the last saved version), never leaves the force flag
   * armed for a later save, and only confirms when the write happened.
   */
  const handleKeepMine = useCallback(async () => {
    if (!applyValidation(readSnapshot())) {
      setToast('Fix the problems above, then choose Keep mine again.');
      return;
    }
    forceNextSaveRef.current = true;
    forcedWriteDoneRef.current = false;
    autosave.clearConflict();
    try {
      const ok = await autosave.saveNow({ force: true });
      if (ok && forcedWriteDoneRef.current) setToast('Your version was saved.');
    } finally {
      forceNextSaveRef.current = false;
      forcedWriteDoneRef.current = false;
    }
  }, [applyValidation, autosave, readSnapshot]);

  const sendToInky = useCallback((prompt) => openInky({ prompt, send: true }), [openInky]);
  const goToTemplates = useCallback(() => navigate('/templates'), [navigate]);

  const menuExtras = useMemo(() => [
    { key: 'help', label: 'How templates work', icon: <HelpOutline fontSize="small" />, onClick: () => setHelpOpen(true) },
  ], []);
  const handleEditorSave = useCallback(() => saveNow(), [saveNow]);

  if (loading) {
    return <TemplateEditorSkeleton onBack={goToTemplates} />;
  }

  const halted = autosave.isHalted();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        '--q-editor-sticky-top': `calc(var(--q-page-sticky-top, 0px) + ${headerHeight}px)`,
      }}
    >
      <Box
        component="header"
        ref={headerRef}
        sx={{
          position: 'sticky',
          top: 'var(--q-page-sticky-top, 0px)',
          zIndex: 20,
          minHeight: HEADER_HEIGHT,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          columnGap: { xs: 0.5, sm: 1 },
          rowGap: 0.5,
          px: { xs: 0.5, sm: 1.5 },
          py: 0.75,
          bgcolor: 'var(--q-surface, #FFFFFF)',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <EditorNavButton />
        <Tooltip title="Back to templates">
          <IconButton aria-label="Back to templates" onClick={goToTemplates}>
            <ArrowBack />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: '1 1 220px', minWidth: 180, display: 'flex', flexDirection: 'column' }}>
          <Box
            component="nav"
            aria-label="Breadcrumb"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 12.5, color: 'text.secondary', minWidth: 0 }}
          >
            <Link
              component="button"
              type="button"
              underline="hover"
              color="inherit"
              onClick={goToTemplates}
              sx={{ fontSize: 'inherit', fontFamily: 'inherit' }}
            >
              Templates
            </Link>
            <span aria-hidden="true">/</span>
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {isEditing ? 'Word document' : 'New template'}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <InputBase
              value={name}
              placeholder={isEditing ? 'Template name' : 'Name your template'}
              onChange={(e) => {
                setName(e.target.value);
                nameRef.current = e.target.value;
                markDirty();
              }}
              inputProps={{ 'aria-label': 'Template name', maxLength: 200 }}
              sx={{
                flex: '1 1 auto',
                maxWidth: 420,
                minWidth: 80,
                fontSize: 17,
                fontWeight: 600,
                letterSpacing: '-0.01em',
                '& input': { px: 0.75, py: '1px', ml: -0.75, borderRadius: '6px', border: '1px solid transparent', textOverflow: 'ellipsis' },
                '& input:hover': { borderColor: 'divider' },
                '& input:focus': { borderColor: 'var(--q-focus-ring, #4F3BD6)' },
              }}
            />
            <SaveStatusLabel
              isEditing={isEditing}
              status={autosave.status}
              newDirty={newDirty}
              creating={creating}
              error={autosave.error}
              lastSavedAt={autosave.lastSavedAt}
              halted={halted}
            />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, ml: 'auto', flex: 'none' }}>
          <EditorJobsButton />
          <TemplateQualityChip quality={templateQuality} onFix={sendToInky} />
          <InkyReopenButton />
          {isEditing ? (
            <Tooltip title="Generate a document from this template">
              <Button
                variant="contained"
                disableElevation
                startIcon={<PostAddOutlined />}
                onClick={handleGenerate}
                aria-label="Generate document"
                sx={{ borderRadius: 999, flex: 'none', whiteSpace: 'nowrap', minWidth: 0, px: { xs: 1.25, sm: 2 }, '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } } }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Generate document</Box>
              </Button>
            </Tooltip>
          ) : (
            <Button
              variant="contained"
              disableElevation
              onClick={() => createTemplate()}
              disabled={creating}
              startIcon={creating ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ borderRadius: 999, flex: 'none', whiteSpace: 'nowrap' }}
            >
              {creating ? 'Creating…' : 'Create template'}
            </Button>
          )}
        </Box>
      </Box>

      {isEditing && autosave.status === 'conflict' && (
        <Alert
          severity="warning"
          sx={{ borderRadius: 0 }}
          action={(
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button color="inherit" size="small" onClick={handleReloadTheirs}>Reload</Button>
              <Button color="inherit" size="small" variant="outlined" onClick={handleKeepMine}>Keep mine</Button>
            </Box>
          )}
        >
          This template was changed in another tab or by a teammate. Reload their version (your unsaved edits are discarded) or keep yours and overwrite.
        </Alert>
      )}

      <Box sx={{ px: { xs: 1, sm: 2 }, pt: 1.5 }}>
        <InputBase
          value={description}
          placeholder="Add a short description (optional)"
          onChange={(e) => {
            setDescription(e.target.value);
            descriptionRef.current = e.target.value;
            markDirty();
          }}
          multiline
          maxRows={3}
          inputProps={{ 'aria-label': 'Template description', maxLength: 1000 }}
          sx={{ width: '100%', maxWidth: 760, fontSize: 14, color: 'text.secondary' }}
        />
        {validation.name && <Alert severity="error" sx={{ mt: 1 }}>{validation.name}</Alert>}
        {validation.content && <Alert severity="error" sx={{ mt: 1 }}>{validation.content}</Alert>}
        {createError && <Alert severity="error" sx={{ mt: 1 }}>{createError}</Alert>}
        {isEditing && autosave.status === 'error' && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {halted
              ? `Not saved: ${autosave.error || 'the last save failed'}. Your changes are kept on this page. Fix the problem, then press ${formatShortcut('Mod+S')} (or Save now in the More menu) to try again.`
              : `Couldn't save: ${autosave.error || 'the last save failed'}. Retrying automatically; your changes are kept on this page.`}
          </Alert>
        )}
        {legacyConverted > 0 && (
          <Alert severity="info" sx={{ mt: 1 }} onClose={() => setLegacyConverted(0)}>
            {legacyConverted} {'{placeholder}'} prompt{legacyConverted === 1 ? ' was' : 's were'} converted into AI fields. Click a field to set its output, source and length. The new format is saved with your next edit ({formatShortcut('Mod+S')} saves now).
          </Alert>
        )}
      </Box>

      <Box sx={{ px: { xs: 0.5, sm: 1 }, pb: 4 }}>
        <EnhancedRichTextEditor
          ref={editorRef}
          mode="template"
          documentKey={loadKey}
          initialContent={initialContent}
          onDirty={markDirty}
          onChange={setLiveContent}
          onSave={handleEditorSave}
          onReady={setEditorReady}
          menuExtras={menuExtras}
          meta={{ templateId: isEditing ? templateId : null, title: name || 'New template' }}
          placeholder="Start your template. Type '/' to add an AI field or a variable."
          minHeight={400}
          enableAdvancedFormatting
          enableImages
        />
      </Box>

      <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth="sm" fullWidth disableRestoreFocus>
        <DialogTitle>How templates work</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" gutterBottom>AI fields</Typography>
          <Typography variant="body2" paragraph>
            Everything you type is copied into generated documents exactly as written. AI only writes inside AI fields.
            Add one with the toolbar "AI field" button or type "/" and choose AI field. Click a field to set its instruction,
            output (paragraphs, list, table rows, short text), source (requirements, sessions, project) and a word limit.
          </Typography>
          <Typography variant="subtitle2" gutterBottom>Variables</Typography>
          <Typography variant="body2" paragraph>
            Variables such as {'{{project.name}}'}, {'{{customer.name}}'} and {'{{doc.date}}'} are filled automatically, never by the AI.
            Insert one from the toolbar's More menu or type "/".
          </Typography>
          <Typography variant="subtitle2" gutterBottom>Locked blocks</Typography>
          <Typography variant="body2" paragraph>
            Lock a paragraph, table or heading with "Lock block" in the toolbar's More menu so Inky never changes it when you ask for edits.
          </Typography>
          <Typography variant="subtitle2" gutterBottom>Saving</Typography>
          <Typography variant="body2" paragraph>
            Saved templates save automatically as you edit ({formatShortcut('Mod+S')} saves now). A new template is saved when you create it.
          </Typography>
          <Typography variant="subtitle2" gutterBottom>Writing good instructions</Typography>
          <Typography variant="body2">
            Be specific, say what the section should cover, and choose the right source. Example: "Summarize the project goals in 2-3 paragraphs based on the requirements."
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={limitReachedOpen} onClose={() => setLimitReachedOpen(false)}>
        <DialogTitle>Template limit reached</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You've reached your template limit. Upgrade your plan to create more templates.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLimitReachedOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              setLimitReachedOpen(false);
              window.dispatchEvent(new Event('quillai:open-settings'));
            }}
          >
            Upgrade
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={1800}
        onClose={() => setToast(null)}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

export default TemplateEditor;
